import {
  useMutation,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

import { campaignJobBoardQueryKeys } from "./queryKeys";
import { updateInfiniteQueryCache } from "../utils/updateInfiniteQueryCache";

import { campaignQueryKeys } from "@/features/campaigns/api/queryKeys";
import { clientQueryKeys } from "@/features/clients/api/query-keys";
import { integrationsKeys } from "@/features/integrations/hooks/useIntegrations";
import { invoiceQueryKeys } from "@/features/invoices/api/queryKeys";
import { segmentsQueryKeys } from "@/features/segments/api/queryKeys";
import { instance } from "@/lib/axios/instance";
import { AddCampaignJobBoardRequest } from "@/types/request/AddCampaignJobBoardRequest";
import { CampaignJobBoardType } from "@/types/response/CampaignJobBoardType";
import { PagedResponse } from "@/types/response/PagedResponse";
import { ValidationErrorResponse } from "@/types/response/ValidationResponse";

type CreateCampaignIntegrationResponse = {
  campaignId: string;
  clientIntegrationModuleId: string;
  clientId2: string | null;
  campaignName: string | null;
  integrationDisplayName: string | null;
};

interface UseAddCampaignJobBoardMutationParams {
  campaignId: string;
  clientId?: string;
}

export function useAddCampaignJobBoardMutation({
  campaignId,
  clientId,
}: UseAddCampaignJobBoardMutationParams) {
  const queryClient = useQueryClient();
  
  // Normalize clientId to match CampaignDetailPage behavior (empty string instead of undefined)
  const normalizedClientId = clientId ?? "";

  return useMutation<
    CreateCampaignIntegrationResponse,
    AxiosError<ValidationErrorResponse>,
    AddCampaignJobBoardRequest
  >({
    mutationFn: (payload) =>
      instance
        .post<CreateCampaignIntegrationResponse>(
          `/api/v2/Campaigns/${campaignId}/Integrations`,
          {
            ...payload,
            campaignId,
            clientId2: clientId,
          }
        )
        .then((response) => response.data),
    onSuccess: (response, variables) => {
      // Optimistically update all infinite query variations for this campaign
      // This handles cases where filters (statuses, search) are applied
      // Query key structure: ["campaignJobBoards", "infinite", campaignId, clientId, statuses, search]
      // Note: CampaignDetailPage uses "" for undefined clientId, so we normalize to match
      queryClient.setQueriesData<
        InfiniteData<PagedResponse<CampaignJobBoardType>>
      >(
        {
          queryKey: campaignJobBoardQueryKeys.infinites(),
          predicate: (query) => {
            const key = query.queryKey;
            // Match queries for this campaignId and clientId (with any filters)
            // key[0] = "campaignJobBoards", key[1] = "infinite", key[2] = campaignId, key[3] = clientId
            if (key.length < 4) return false;
            
            const queryCampaignId = key[2];
            const queryClientId = key[3];
            
            // Normalize both sides: treat undefined, null, and empty string as equivalent
            // Query key might have undefined or "" depending on how it was created
            const normalizedQueryClientId = 
              queryClientId === undefined || queryClientId === null || queryClientId === ""
                ? ""
                : queryClientId;
            
            return (
              queryCampaignId === campaignId &&
              normalizedQueryClientId === normalizedClientId
            );
          },
        },
        (old) => {
          if (!old) return old;
          return updateInfiniteQueryCache(old, response, variables, clientId);
        }
      );
      
      // Also directly update the base query (no filters) to ensure it's updated
      const baseQueryKey = campaignJobBoardQueryKeys.infinite(
        campaignId,
        normalizedClientId
      );
      queryClient.setQueryData<InfiniteData<PagedResponse<CampaignJobBoardType>>>(
        baseQueryKey,
        (old) => {
          if (!old) return old;
          return updateInfiniteQueryCache(old, response, variables, clientId);
        }
      );
      
      // Invalidate list query to refresh disabled jobboards
      queryClient.invalidateQueries({
        queryKey: campaignJobBoardQueryKeys.list(campaignId, clientId),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: campaignJobBoardQueryKeys.all,
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: campaignJobBoardQueryKeys.clientBudgetMonth.all,
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: campaignJobBoardQueryKeys.months.all,
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: campaignJobBoardQueryKeys.monthsByJobBoard.all,
        exact: false,
      });
      queryClient.invalidateQueries({ queryKey: campaignQueryKeys.all, exact: false });
      queryClient.invalidateQueries({ queryKey: clientQueryKeys.all, exact: false });
      queryClient.invalidateQueries({
        queryKey: invoiceQueryKeys.cache.transactions.all(),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: integrationsKeys.all,
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: segmentsQueryKeys.all,
        exact: false,
      });
    },
  });
}
